import React from 'react';
import { tv } from 'tailwind-variants';
import { IconProps } from '@phosphor-icons/react';
import { ButtonCommon } from '../Button/ButtonCommon';
import { useNavigate, useLocation } from 'react-router-dom';

interface TabOption {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path: string;
}

interface TabSwitcherProps {
  options: TabOption[];
  defaultActiveKey?: string;
  className?: string;
  activeColorClass?: string;
  inactiveColorClass?: string;
}

const tabButton = tv({
  base: 'h-10 px-4 py-2 flex justify-center items-center gap-2',
  variants: {
    active: {
      true: 'bg-blue-500',
      false: 'bg-black/5 border-black/10',
    },
    radius: {
      rounded: 'rounded-lg',
      square: 'rounded-none',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    active: false,
  },
});

const tabIcon = tv({
  variants: {
    active: {
      true: 'text-white',
      false: 'text-black',
    },
  },
});

const tabText = tv({
  base: 'justify-start',
  variants: {
    active: {
      true: 'text-white text-base font-medium leading-snug',
      false: 'text-black/80 text-base font-normal leading-snug',
    },
  },
});

const TabSwitcher: React.FC<TabSwitcherProps> = ({ options, className = '', activeColorClass, inactiveColorClass }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleTabChange = (path: string) => {
    navigate(path);
  };

  // tab active check path and query string
  const isTabActive = (tabPath: string) => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const hasQueryInTab = tabPath.includes('?');
    const currentHasQuery = location.search !== '';

    if (hasQueryInTab) {
      const [basePath, queryString] = tabPath.split('?');
      const pathMatches = pathname === basePath;

      if (!currentHasQuery) {
        return false;
      }

      const tabParams = new URLSearchParams(queryString);
      let allParamsMatch = true;

      for (const [key, value] of tabParams.entries()) {
        if (searchParams.get(key) !== value) {
          allParamsMatch = false;
          break;
        }
      }

      return pathMatches && allParamsMatch;
    } else {
      const hasMatchingQueryTab = options.some((opt) => {
        if (!opt.path.includes('?')) return false;

        const [optBasePath, optQueryString] = opt.path.split('?');
        if (optBasePath !== pathname) return false;

        if (!currentHasQuery) return false;

        const optParams = new URLSearchParams(optQueryString);
        let allMatch = true;
        for (const [key, value] of optParams.entries()) {
          if (searchParams.get(key) !== value) {
            allMatch = false;
            break;
          }
        }
        return allMatch;
      });

      if (pathname === tabPath) {
        if (currentHasQuery && hasMatchingQueryTab) {
          return false;
        }
        return true;
      }

      if (pathname.startsWith(tabPath + '/')) {
        const isMoreSpecificTabAvailable = options.some((opt) => {
          const optPath = opt.path.split('?')[0];
          return optPath !== tabPath && pathname === optPath && optPath.startsWith(tabPath + '/');
        });
        return !isMoreSpecificTabAvailable;
      }

      return false;
    }
  };

  return (
    <div className={`inline-flex justify-start items-start rounded-lg ${className}`}>
      {options.map((option) => {
        const isActive = isTabActive(option.path);
        return (
          <ButtonCommon
            key={option.key}
            radius='rounded'
            className={
              tabButton({ active: isActive }) +
              ' cursor-pointer ' +
              (isActive ? (activeColorClass ?? '') : (inactiveColorClass ?? '!bg-transparent'))
            }
            onClick={() => handleTabChange(option.path)}
          >
            {option.icon && (
              <div className={tabIcon({ active: isActive })}>
                {React.isValidElement(option.icon) &&
                  React.cloneElement<IconProps>(option.icon as React.ReactElement<IconProps>, {
                    size: 16,
                    className: isActive ? 'text-white' : 'text-black',
                  })}
              </div>
            )}
            <div className='flex justify-start text-[16px] leading-[22px] font-medium items-center'>
              <div className={tabText({ active: isActive }) + ' text-nowrap '}>{option.label}</div>
            </div>
          </ButtonCommon>
        );
      })}
    </div>
  );
};

export default TabSwitcher;
